<script setup lang="ts">
/**
 * API keys — the credential MAD and other external admin systems authenticate
 * with (`x-api-key`).
 *
 * **A created key is shown once and never again.** The table stores a SHA-256
 * hash, so there is nothing to show later; the panel holds the plaintext in a
 * ref until the operator navigates away. That is stated on screen, because a
 * key silently lost is a key regenerated and an integration broken.
 */
import { ref } from 'vue';

import { adminApi } from './admin-api';
import { usePane, when } from './pane';

const keys = usePane(() => adminApi.keys());
const label = ref('');
const fresh = ref<string | null>(null);

function create(): void {
  const name = label.value.trim();
  if (!name) return;
  void keys.act(async () => {
    const made = await adminApi.createKey(name);
    fresh.value = made.key;
    label.value = '';
  });
}

function revoke(id: string, name: string): void {
  if (!window.confirm(`Revoke "${name}"? Anything using it stops working immediately.`)) return;
  void keys.act(() => adminApi.deleteKey(id));
}
</script>

<template>
  <h1>API keys</h1>
  <p class="lede">
    How MAD and other external admin systems authenticate against this server.
  </p>

  <p v-if="keys.error.value" class="bad">{{ keys.error.value }}</p>

  <section v-if="fresh">
    <h2>New key</h2>
    <div class="scroller" style="padding: 0.7rem">
      <code style="word-break: break-all; color: var(--accent)">{{ fresh }}</code>
    </div>
    <p class="note">
      Copy it now. Only a hash is stored, so this is the only time it can be
      shown.
    </p>
    <div class="toolbar" style="margin-top: 0.4rem">
      <button @click="fresh = null">Done</button>
    </div>
  </section>

  <section>
    <h2>Keys</h2>
    <div class="scroller">
      <table data-test="admin-keys-table">
        <thead><tr><th>Label</th><th>Created</th><th>Last used</th><th>Actions</th></tr></thead>
        <tbody>
          <tr v-for="row in keys.data.value ?? []" :key="row.id">
            <td>{{ row.label }}</td>
            <td>{{ when(row.createdAt) }}</td>
            <td>{{ when(row.lastUsedAt) }}</td>
            <td class="actions">
              <button class="danger" @click="revoke(row.id, row.label)">Revoke</button>
            </td>
          </tr>
          <tr v-if="!keys.loading.value && (keys.data.value?.length ?? 0) === 0">
            <td colspan="4" class="empty">No keys issued.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="toolbar" style="margin-top: 0.5rem">
      <input v-model="label" type="text" placeholder="What is it for?" style="min-width: 14rem" />
      <button class="accent" :disabled="keys.loading.value" @click="create">Issue a key</button>
    </div>
  </section>
</template>
